import { useEffect } from 'react'
import type { RefObject } from 'react'
import { router } from '@inertiajs/react'
import { InvestigationReadScope } from '@/frontend/features/Fde/investigations/InvestigationReadScope'
import { isInvestigationRefresh } from '@/frontend/features/Fde/investigations/isInvestigationRefresh'

export function useInvestigationRecovery(scope: string, marker: string, reads: RefObject<InvestigationReadScope>, refresh: () => void,
  setFailed: (value: boolean) => void, setUnavailable: (value: boolean) => void) {
  useEffect(() => {
    const owned = reads.current.closed ? new InvestigationReadScope() : reads.current
    reads.current = owned
    // Inertia async reloads compare pathname, not project query parameters.
    // Fence before navigation starts, not just after the old screen unmounts.
    const before = router.on('before', (event) => {
      if (!isInvestigationRefresh({ headers: event.detail.visit.headers }, marker)) owned.pause()
    })
    const finish = router.on('finish', (event) => {
      if (isInvestigationRefresh({ headers: event.detail.visit.headers }, marker)) return
      if (new URLSearchParams(window.location.search).get('project_id') === scope) owned.resume()
    })
    const invalid = router.on('invalid', (event) => {
      if (!isInvestigationRefresh(event.detail.response.config, marker)) return
      event.preventDefault()
      if ([401, 403, 404].includes(event.detail.response.status)) setUnavailable(true)
      else setFailed(true)
    })
    const exception = router.on('exception', (event) => {
      const error = event.detail.exception as Error & { config?: unknown }
      if (!isInvestigationRefresh(error.config, marker)) return
      event.preventDefault(); setFailed(true)
    })
    window.addEventListener('online', refresh)
    window.addEventListener('focus', refresh)
    return () => {
      owned.dispose()
      before(); finish(); invalid(); exception()
      window.removeEventListener('online', refresh)
      window.removeEventListener('focus', refresh)
    }
  }, [scope, marker, reads, refresh, setFailed, setUnavailable])
}
